import React from "react";
import { Card } from "@/components/ui/card";
import { Database, Brain, Network, Search, Zap, FileText, RotateCw, ArrowDown } from "lucide-react";

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      icon: Database,
      title: "Data Ingestion",
      description: "Captures telemetry from XDR, EDR, SIEM, identity management, and cloud environments, bringing every signal into one security fabric.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      number: "02",
      icon: Brain,
      title: "Spharaka LLM Reasoning Layer",
      description: "Processes and interprets security events using natural language understanding, contextual correlation, and cognitive reasoning for human-like decision-making.",
      color: "from-purple-500 to-pink-500"
    },
    {
      number: "03",
      icon: Network,
      title: "Threat Intelligence Correlation",
      description: "Correlates data across internal systems, external threat feeds, and behavioral baselines to identify potential patterns and hidden risks before they escalate.",
      color: "from-orange-500 to-red-500"
    },
    {
      number: "04",
      icon: Search,
      title: "Autonomous Investigation",
      description: "Automatically investigates incidents by mapping behavioral anomalies, event timelines, and system interactions to determine intent, impact, and origin, drastically reducing analyst workload and mean time to respond (MTTR).",
      color: "from-green-500 to-emerald-500"
    },
    {
      number: "05",
      icon: Zap,
      title: "Autonomous Action Engine",
      description: "Executes containment, remediation, and adaptive response actions in real time, turning intelligence into immediate defensive action.",
      color: "from-yellow-500 to-amber-500"
    },
    {
      number: "06",
      icon: FileText,
      title: "Organizational Memory Layer",
      description: "Retains every decision, correlation, and learning outcome, ensuring that your organization's cyber intelligence persists even as teams evolve.",
      color: "from-indigo-500 to-blue-500"
    },
    {
      number: "07",
      icon: RotateCw,
      title: "Continuous Learning Loop",
      description: "Enhances models and reasoning logic through ongoing feedback and pattern recognition, making AuraXP smarter with every interaction.",
      color: "from-pink-500 to-purple-500"
    }
  ];

  return (
    <section className="py-12 md:py-16 bg-gradient-to-b from-background via-muted/20 to-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#8932c8] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="text-center space-y-3">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              How AuraXP Works
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
              AuraXP creates a unified security intelligence layer across your enterprise infrastructure.
            </p>
          </div>

          {/* Vertical flow diagram */}
          <div className="relative">
            {/* Connecting line */}
            <div className="absolute left-6 md:left-10 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#8932c8] via-blue-500 to-[#8932c8] opacity-20"></div>

            <div className="space-y-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="relative">
                    {/* Step card */}
                    <Card className="ml-0 md:ml-6 p-4 md:p-6 hover:shadow-xl transition-all duration-300 border hover:border-[#8932c8]/50 bg-card/80 backdrop-blur">
                      <div className="flex flex-col md:flex-row gap-4 items-start">
                        {/* Icon circle with gradient */}
                        <div className="flex-shrink-0">
                          <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-md`}>
                            <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 space-y-1.5">
                          <h3 className="text-lg md:text-xl font-bold text-foreground bg-gradient-to-r from-[#8932c8] to-blue-600 bg-clip-text text-transparent">
                            {step.title}
                          </h3>
                          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </Card>

                    {/* Arrow connector */}
                    {index < steps.length - 1 && (
                      <div className="flex justify-center my-2 md:ml-6">
                        <ArrowDown className="w-5 h-5 text-[#8932c8]/50" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center pt-4">
            <div className="inline-block p-6 rounded-xl bg-gradient-to-r from-[#8932c8]/10 to-blue-500/10 border border-[#8932c8]/20">
              <p className="text-lg md:text-xl font-semibold text-foreground mb-1">
                Every alert becomes intelligence. Every action becomes memory.
              </p>
              <p className="text-base md:text-lg font-medium text-[#8932c8]">
                Every decision strengthens defense.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
